import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PartyGroup, PartyGroupCreate, PartyGroupUpdate } from "@/app/types/party";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PartyGroupForm from "./PartyGroupForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listPartyGroups, createPartyGroup, updatePartyGroup, deletePartyGroup } from "@/app/services/partyService";

interface PartyGroupManagementProps {
  onGroupsChange?: () => void;
}

const PartyGroupManagement: React.FC<PartyGroupManagementProps> = ({
  onGroupsChange
}) => {
  const queryClient = useQueryClient();
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<PartyGroup | null>(null);

  // Query to fetch all party groups
  const {
    data: groupsResponse,
    isLoading: isGroupsLoading,
  } = useQuery({
    queryKey: ['partyGroups'],
    queryFn: () => listPartyGroups({
      skip: 0,
      limit: 100,
      sort_by: 'name',
      sort_dir: 'asc'
    }),
  });

  // Mutation for creating a party group
  const createMutation = useMutation({
    mutationFn: (data: PartyGroupCreate) => createPartyGroup(data),
    onSuccess: () => {
      toast.success('Party group created successfully');
      queryClient.invalidateQueries({ queryKey: ['partyGroups'] });
      setIsCreateFormOpen(false);
      if (onGroupsChange) onGroupsChange();
    },
    onError: (error) => {
      toast.error(`Failed to create party group: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Mutation for updating a party group
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PartyGroupUpdate }) => updatePartyGroup(id, data),
    onSuccess: () => {
      toast.success('Party group updated successfully');
      queryClient.invalidateQueries({ queryKey: ['partyGroups'] });
      setIsEditFormOpen(false);
      setCurrentGroup(null);
      if (onGroupsChange) onGroupsChange();
    },
    onError: (error) => {
      toast.error(`Failed to update party group: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Mutation for deleting a party group
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePartyGroup(id),
    onSuccess: () => {
      toast.success('Party group deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['partyGroups'] });
      queryClient.invalidateQueries({ queryKey: ['parties'] });
      setIsDeleteDialogOpen(false);
      setCurrentGroup(null);
      if (onGroupsChange) onGroupsChange();
    },
    onError: (error) => {
      toast.error(`Failed to delete party group: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const handleCreateClick = (): void => {
    setIsCreateFormOpen(true);
  };

  const handleEditClick = (group: PartyGroup): void => {
    setCurrentGroup(group);
    setIsEditFormOpen(true);
  };

  const handleDeleteClick = (group: PartyGroup): void => {
    setCurrentGroup(group);
    setIsDeleteDialogOpen(true);
  };

  const handleCreateSubmit = (formData: PartyGroupCreate | PartyGroupUpdate): void => {
    createMutation.mutate(formData as PartyGroupCreate);
  };

  const handleEditSubmit = (formData: PartyGroupUpdate): void => {
    if (currentGroup) {
      updateMutation.mutate({ id: currentGroup.id, data: formData });
    }
  };

  const handleDeleteConfirm = (): void => {
    if (currentGroup) {
      deleteMutation.mutate(currentGroup.id);
    }
  };

  const groups = groupsResponse?.items || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Party Groups</CardTitle>
          <CardDescription>
            Organize your parties into groups for easier management
          </CardDescription>
        </div>
        <Button onClick={handleCreateClick} size="sm">
          <Plus className="mr-2 h-4 w-4" /> Add Group
        </Button>
      </CardHeader>
      <CardContent>
        {isGroupsLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No groups found. Create a group to get started.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {groups.map((group) => (
              <div key={group.id} className="flex items-center gap-2 bg-secondary/20 rounded-lg p-2">
                <Badge variant="outline" className="font-normal">
                  {group.name}
                </Badge>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleEditClick(group)}
                  >
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => handleDeleteClick(group)}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Delete</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create Form */}
      <PartyGroupForm
        open={isCreateFormOpen}
        onOpenChange={setIsCreateFormOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        mode="create"
      />
      
      {/* Edit Form */}
      {currentGroup && (
        <PartyGroupForm
          open={isEditFormOpen}
          onOpenChange={(open) => {
            setIsEditFormOpen(open);
            if (!open) setCurrentGroup(null);
          }}
          onSubmit={handleEditSubmit}
          defaultValues={currentGroup}
          isLoading={updateMutation.isPending}
          mode="edit"
        />
      )}
      
      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the party group "{currentGroup?.name}".
              Any parties assigned to this group will be unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default PartyGroupManagement; 